import { useCallback } from 'react';
import { useAuthStore, type AuthUser } from '@/lib/auth';
import { post, getErrorMessage } from '@/lib/api';
import { disconnectSocket } from '@/lib/socket';

interface OtpRequestResponse {
  success: true;
  ttl_seconds: number;
  dev_otp?: string;
}

interface OtpVerifyResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: 'Bearer';
  user: AuthUser;
}

export function useAuth(): {
  user: AuthUser | null;
  isAuthenticated: boolean;
  requestOtp: (phone: string) => Promise<OtpRequestResponse>;
  verifyOtp: (phone: string, otp: string) => Promise<OtpVerifyResponse>;
  logout: () => Promise<void>;
} {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const clear = useAuthStore((s) => s.clear);

  const requestOtp = useCallback(async (phone: string) => {
    try {
      return await post<OtpRequestResponse>('/auth/otp/request', { phone, purpose: 'login' });
    } catch (e) {
      throw new Error(getErrorMessage(e));
    }
  }, []);

  const verifyOtp = useCallback(
    async (phone: string, otp: string) => {
      try {
        const res = await post<OtpVerifyResponse>('/auth/otp/verify', { phone, otp });
        setSession({
          accessToken: res.access_token,
          refreshToken: res.refresh_token,
          expiresIn: res.expires_in,
          user: res.user
        });
        return res;
      } catch (e) {
        throw new Error(getErrorMessage(e));
      }
    },
    [setSession]
  );

  const logout = useCallback(async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (refreshToken) {
      try {
        await post('/auth/logout', { refresh_token: refreshToken });
      } catch {
        /* swallow — local clear is the source of truth */
      }
    }
    disconnectSocket();
    clear();
  }, [clear]);

  return {
    user,
    isAuthenticated: Boolean(accessToken && user),
    requestOtp,
    verifyOtp,
    logout
  };
}
